/**
 * Connections: everything the CMS talks to, its state in plain words, and what happened the last few times.
 *
 * Pure. The worker gathers facts from its own records (saved keys, background-job status, run records) and this turns them
 * into rows. A connection that was never used has no runs, and the line says so; nothing is guessed.
 */
import { z } from "zod";

export const CONNECTION_IDS = ["ai", "sociamonials", "apify", "semrush", "google_results", "google_file", "linkedin_results", "linkedin_posts"] as const;
export const connectionIdSchema = z.enum(CONNECTION_IDS);
export type ConnectionId = (typeof CONNECTION_IDS)[number];

export const connectionRunSchema = z.object({ at: z.string(), what: z.string(), ok: z.boolean() });

export const connectionSchema = z.object({
  id: connectionIdSchema,
  name: z.string(),
  what: z.string(),
  /** ok: working. warn: needs a look. off: not connected. */
  state: z.enum(["ok", "warn", "off"]),
  line: z.string(),
  runs: z.array(connectionRunSchema),
  /** Where a manager goes to fix or set it up. */
  action: z.object({ label: z.string(), to: z.enum(["api_keys", "ads_setup", "social_channels", "ads_results"]) }).nullable(),
});
export type Connection = z.infer<typeof connectionSchema>;
export const connectionsResponseSchema = z.object({ items: z.array(connectionSchema) });

export interface ConnectionFacts {
  now: string;
  keys: Record<"anthropic" | "xai" | "sociamonials" | "apify" | "semrush", boolean>;
  aiLastCallAt: string | null;
  social: { lastRunAt: string | null; lastOkAt: string | null; lastError: string | null; lastErrorAt: string | null };
  apifyRuns: Array<{ at: string; status: "running" | "succeeded" | "partial" | "failed"; note: string | null }>;
  semrushLastAt: string | null;
  google: { connected: boolean; lastUsedAt: string | null; receipts: Array<{ at: string; what: string }> };
  files: Array<{ at: string; what: string }>;
  linkedinResultRows: number;
  linkedin: { keysSaved: boolean; lastRunAt: string | null; lastOkAt: string | null; lastError: string | null; lastErrorAt: string | null; receipts: Array<{ at: string; what: string }> };
}

const DAY = 86_400_000;
const ago = (iso: string, now: string): string => {
  const d = Math.floor((Date.parse(now) - Date.parse(iso)) / DAY);
  if (!Number.isFinite(d) || d < 0) return "recently";
  return d === 0 ? "today" : d === 1 ? "yesterday" : `${d} days ago`;
};

export function buildConnections(f: ConnectionFacts): Connection[] {
  const out: Connection[] = [];

  const aiKeys = [f.keys.anthropic && "Claude", f.keys.xai && "Grok"].filter(Boolean) as string[];
  out.push({
    id: "ai", name: "Claude and Grok", what: "Writes ideas, ads and posts",
    state: aiKeys.length === 0 ? "off" : "ok",
    line: aiKeys.length === 0 ? "No key saved yet" : `${aiKeys.join(" and ")} connected${f.aiLastCallAt ? ` · last used ${ago(f.aiLastCallAt, f.now)}` : ""}`,
    runs: [], action: aiKeys.length === 0 ? { label: "Add a key", to: "api_keys" } : null,
  });

  const smFailed = !!f.social.lastError && (!f.social.lastOkAt || (f.social.lastErrorAt ?? "") > f.social.lastOkAt);
  out.push({
    id: "sociamonials", name: "Sociamonials", what: "Sends approved social posts",
    state: !f.keys.sociamonials ? "off" : smFailed ? "warn" : "ok",
    line: !f.keys.sociamonials ? "No key saved yet" : smFailed ? `The last run failed${f.social.lastErrorAt ? ` ${ago(f.social.lastErrorAt, f.now)}` : ""}. ${f.social.lastError}` : f.social.lastOkAt ? `Last ran ${ago(f.social.lastOkAt, f.now)}` : "Connected. Nothing sent yet",
    runs: f.social.lastOkAt ? [{ at: f.social.lastOkAt, what: "Checked the status of sent posts", ok: true }] : [],
    action: !f.keys.sociamonials ? { label: "Add a key", to: "api_keys" } : smFailed ? { label: "Open channels", to: "social_channels" } : null,
  });

  const lastApify = f.apifyRuns[0];
  out.push({
    id: "apify", name: "LinkedIn Ad Library (Apify)", what: "Finds competitor ads each week",
    state: !f.keys.apify ? "off" : lastApify && lastApify.status === "failed" ? "warn" : "ok",
    line: !f.keys.apify ? "No key saved yet" : lastApify ? (lastApify.status === "failed" ? `The last run failed ${ago(lastApify.at, f.now)}` : `Last ran ${ago(lastApify.at, f.now)}`) : "Connected. Hasn't run yet",
    runs: f.apifyRuns.map((r) => ({ at: r.at, ok: r.status === "succeeded", what: r.status === "succeeded" ? "Found competitor ads" : r.status === "partial" ? `Found some ads. ${r.note ?? "Some were skipped."}` : r.status === "running" ? "Running…" : `Failed${r.note ? `: ${r.note}` : ""}` })),
    action: !f.keys.apify ? { label: "Add a key", to: "api_keys" } : null,
  });

  out.push({
    id: "semrush", name: "Semrush", what: "Keyword volume and cost",
    state: f.keys.semrush ? "ok" : "off",
    line: f.keys.semrush ? (f.semrushLastAt ? `Last used ${ago(f.semrushLastAt, f.now)}` : "Connected. Not used yet") : "No key saved yet",
    runs: [], action: f.keys.semrush ? null : { label: "Add a key", to: "api_keys" },
  });

  out.push({
    id: "google_results", name: "Google Ads results", what: "Sends last week's results every Monday",
    state: !f.google.connected ? "off" : !f.google.lastUsedAt ? "warn" : Date.parse(f.now) - Date.parse(f.google.lastUsedAt) > 9 * DAY ? "warn" : "ok",
    line: !f.google.connected ? "Not connected" : !f.google.lastUsedAt ? "Connected, but no results have arrived. Run the script once in Google Ads" : Date.parse(f.now) - Date.parse(f.google.lastUsedAt) > 9 * DAY ? `No results for more than a week. Last ${ago(f.google.lastUsedAt, f.now)}` : `Last results ${ago(f.google.lastUsedAt, f.now)}`,
    runs: f.google.receipts.map((r) => ({ at: r.at, what: r.what, ok: true })),
    action: f.google.connected && f.google.lastUsedAt && Date.parse(f.now) - Date.parse(f.google.lastUsedAt) <= 9 * DAY ? null : { label: f.google.connected ? "Open Google results" : "Connect Google Ads", to: "ads_setup" },
  });

  out.push({
    id: "google_file", name: "Google Ads file", what: "Your weekly upload to Google Ads Editor",
    state: f.files.length > 0 ? "ok" : "off",
    line: f.files.length > 0 ? `Last file made ${ago(f.files[0]!.at, f.now)}` : "No file made yet",
    runs: f.files.map((r) => ({ at: r.at, what: r.what, ok: true })), action: null,
  });

  const li = f.linkedin;
  const liFailed = !!li.lastError && (!li.lastOkAt || (li.lastErrorAt ?? "") > li.lastOkAt);
  out.push({
    id: "linkedin_results", name: "LinkedIn Ads results", what: "Views, clicks and sign-ups for LinkedIn ads",
    state: !li.keysSaved ? "off" : liFailed ? "warn" : "ok",
    line: !li.keysSaved
      ? (f.linkedinResultRows > 0 ? `${f.linkedinResultRows} rows saved. Not connected for automatic weekly results` : "Not connected. Results come from a CSV or are typed in")
      : liFailed ? `The last pull failed${li.lastErrorAt ? ` ${ago(li.lastErrorAt, f.now)}` : ""}. ${li.lastError}`
      : li.lastOkAt ? `Last pulled ${ago(li.lastOkAt, f.now)}` : "Connected. First pull happens on the next weekly run",
    runs: [
      ...li.receipts.map((r) => ({ at: r.at, what: r.what, ok: true })),
      ...(liFailed && li.lastErrorAt ? [{ at: li.lastErrorAt, what: `Failed: ${li.lastError}`, ok: false }] : []),
    ].sort((a, b) => b.at.localeCompare(a.at)).slice(0, 5),
    action: !li.keysSaved ? { label: "Add LinkedIn keys", to: "api_keys" } : liFailed ? { label: "Check the keys", to: "api_keys" } : null,
  });
  out.push({
    id: "linkedin_posts", name: "LinkedIn post results", what: "Clicks, likes and comments for your social posts",
    state: "off",
    line: "Not connected. Sociamonials reports delivery only. This needs a LinkedIn company page admin and LinkedIn's Community Management access",
    runs: [], action: null,
  });
  return out;
}
